import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ImagePlaceholder } from "@/components/ui/image-placeholder";
import { useAuth } from "@/contexts/AuthContext";
import { useFavorites } from "@/hooks/useFavorites";
import type { RestaurantWithEventSpaces } from "@/types/restaurant";
import { useIsMobile } from "@/hooks/use-mobile";

interface FeaturedRestaurantCardProps {
  restaurant: RestaurantWithEventSpaces;
}

export const FeaturedRestaurantCard = ({ restaurant }: FeaturedRestaurantCardProps) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { session } = useAuth();
  const { toggleFavorite, isFavorited } = useFavorites();
  const [hasError, setHasError] = useState(false);

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!session) {
      navigate('/auth');
      return;
    }
    toggleFavorite(restaurant.id);
  };

  const handleError = () => {
    setHasError(true);
  };

  return (
    <div
      key={restaurant.id}
      className="cursor-pointer group relative"
      onClick={() => navigate(`/restaurant/${restaurant.id}`)}
    >
      <div className="relative h-24 rounded-lg overflow-hidden">
        <Button
          variant="ghost"
          size="icon"
          className="favorite-button absolute top-1 right-1 z-10 bg-white/80 hover:bg-white/90 h-8 w-8"
          onClick={handleFavoriteClick}
        >
          <Heart 
            className={`h-4 w-4 ${isFavorited(restaurant.id) ? 'fill-red-500 text-red-500' : 'text-gray-500'}`}
          />
        </Button>
        {!hasError && restaurant.photos?.[0] ? (
          <img
            src={restaurant.photos[0]}
            alt={restaurant.name}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={handleError}
            sizes="(max-width: 640px) 220px, 320px"
          />
        ) : (
          <ImagePlaceholder name={restaurant.name} className="rounded-lg" />
        )}
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="text-white text-sm font-medium">View Details</span>
        </div>
      </div>
      <div className="mt-2">
        <h3 className={`font-medium truncate ${isMobile ? 'text-sm' : 'text-base'}`}>{restaurant.name}</h3>
        <p className={`text-muted-foreground truncate ${isMobile ? 'text-xs' : 'text-sm'}`}>
          {restaurant.neighborhood?.name || 'Location not specified'}
        </p>
      </div>
    </div>
  );
};